/* SECOND SPRING — PDP hero.
   Gallery, tier radiogroup, subscribe toggle, add to cart.
   The supplement facts panel is server-rendered inline under the gallery now, so there
   is no modal to wire up.
   IIFE. Inits on DOMContentLoaded and shopify:section:load. No jQuery, no libraries. */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-ss-pdp-hero]';

  /* ---------- helpers ---------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* ---------- gallery ---------- */

  function initGallery(section) {
    var track = section.querySelector('[data-gallery-track]');
    if (!track) return;

    var slides = toArray(track.querySelectorAll('[data-slide]'));
    var thumbs = toArray(section.querySelectorAll('[data-thumb]'));
    var prevBtn = section.querySelector('[data-gallery-prev]');
    var nextBtn = section.querySelector('[data-gallery-next]');
    var current = 0;

    function paint() {
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === current);
        thumb.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }

    function goTo(index) {
      if (!slides.length) return;
      current = (index + slides.length) % slides.length;
      track.scrollTo({ left: slides[current].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      paint();
    }

    /* Swipe is native scroll snapping; keep state in sync as she swipes. */
    track.addEventListener('scroll', debounce(function () {
      if (!track.clientWidth) return;
      var index = Math.round(track.scrollLeft / track.clientWidth);
      index = Math.max(0, Math.min(slides.length - 1, index));
      if (index !== current) {
        current = index;
        paint();
      }
    }, 80));

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current + 1); }
    });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    /* Deferred video hydration: the video/iframe does not exist until she taps play. */
    toArray(section.querySelectorAll('[data-video-slide]')).forEach(function (slide) {
      var playBtn = slide.querySelector('[data-video-play]');
      var template = slide.querySelector('[data-video-template]');
      if (!playBtn || !template) return;

      playBtn.addEventListener('click', function () {
        var poster = slide.querySelector('[data-video-poster]');
        slide.appendChild(template.content.cloneNode(true));
        if (poster) poster.remove();
        var video = slide.querySelector('video');
        if (video) {
          video.setAttribute('playsinline', '');
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        }
      }, { once: true });
    });
  }

  /* ---------- tier selector + subscribe toggle ---------- */

  function initTiers(section) {
    var root = section.querySelector('[data-offer-tiers]');
    if (!root) return null;

    var tiers = toArray(root.querySelectorAll('[data-tier]'));
    var toggle = root.querySelector('[data-subscribe-toggle]');
    if (!tiers.length) return null;

    function subscribeOn() {
      return !!(toggle && toggle.checked);
    }

    function activeTier() {
      return root.querySelector('[data-tier][aria-checked="true"]') || tiers[0];
    }

    function displayedPrice(tier) {
      var attr = subscribeOn() ? 'data-price-sub' : 'data-price-onetime';
      return tier.getAttribute(attr) || tier.getAttribute('data-price-onetime') || '';
    }

    /* The sticky ATC bar listens for this to keep its price in sync. */
    function announce() {
      var tier = activeTier();
      var labelEl = tier.querySelector('.ss-tier__label');
      document.dispatchEvent(new CustomEvent('ss:tier:change', {
        bubbles: true,
        detail: {
          price: displayedPrice(tier),
          variantId: tier.getAttribute('data-variant-id') || '',
          label: labelEl ? labelEl.textContent.trim() : ''
        }
      }));
    }

    /* Swap [data-tier-price] only. Per-day and savings stay one-time. */
    function paintPrices() {
      tiers.forEach(function (tier) {
        var priceEl = tier.querySelector('[data-tier-price]');
        if (!priceEl) return;
        var price = displayedPrice(tier);
        if (price) priceEl.textContent = price;
      });
    }

    function select(tier, focus) {
      tiers.forEach(function (t) {
        var active = t === tier;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-checked', active ? 'true' : 'false');
        t.tabIndex = active ? 0 : -1;
      });
      if (focus) tier.focus();
      announce();
    }

    /* Roving tabindex. The server already rendered the preselection, do not re-click it. */
    var initial = activeTier();
    tiers.forEach(function (t) { t.tabIndex = t === initial ? 0 : -1; });

    tiers.forEach(function (tier, i) {
      tier.addEventListener('click', function () { select(tier, false); });
      tier.addEventListener('keydown', function (event) {
        var next = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = tiers[(i + 1) % tiers.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = tiers[(i - 1 + tiers.length) % tiers.length];
            break;
          case 'Home':
            next = tiers[0];
            break;
          case 'End':
            next = tiers[tiers.length - 1];
            break;
          default:
            return;
        }
        event.preventDefault();
        select(next, true);
      });
    });

    if (toggle) {
      toggle.addEventListener('change', function () {
        paintPrices();
        announce();
      });
      /* If subscription_default_on shipped the box checked, the server still rendered
         one-time prices. Align the display once, without touching the tier selection. */
      if (toggle.checked) paintPrices();
    }

    /* Once on init so the sticky bar has a correct value before any interaction. */
    announce();

    return {
      activeTier: activeTier,
      subscribeOn: subscribeOn
    };
  }

  /* ---------- add to cart ---------- */

  function initAddToCart(section, tiersApi) {
    var button = section.querySelector('[data-add-to-cart]');
    if (!button) return;

    var labelEl = button.querySelector('span');
    var originalLabel = labelEl ? labelEl.textContent : '';
    var successLabel = section.getAttribute('data-atc-success') || 'Added to cart';
    var errorLabel = section.getAttribute('data-atc-error') || 'Something went wrong, try again';
    var busy = false;
    var revertTimer = null;

    /* Show a state briefly, then restore. Always re-enables: the button is never
       left permanently disabled. */
    function flash(text) {
      busy = false;
      button.removeAttribute('disabled');
      if (labelEl) {
        labelEl.textContent = text;
        clearTimeout(revertTimer);
        revertTimer = setTimeout(function () {
          labelEl.textContent = originalLabel;
        }, 2400);
      }
    }

    button.addEventListener('click', function () {
      if (busy || !tiersApi) return;

      var tier = tiersApi.activeTier();
      if (!tier) { flash(errorLabel); return; }

      var variantId = parseInt(tier.getAttribute('data-variant-id'), 10);
      if (!variantId) { flash(errorLabel); return; }

      /* data-quantity is bottles in the pack, display math only. The cart line
         quantity comes from data-cart-quantity: each tier is its own variant
         carrying the pack price. */
      var item = {
        id: variantId,
        quantity: parseInt(tier.getAttribute('data-cart-quantity'), 10) || 1
      };

      var sellingPlan = tier.getAttribute('data-selling-plan');
      if (tiersApi.subscribeOn() && sellingPlan) {
        item.selling_plan = parseInt(sellingPlan, 10);
      }

      busy = true;
      button.setAttribute('disabled', '');

      var url = (window.SecondSpring && window.SecondSpring.routes && window.SecondSpring.routes.cart_add) || '/cart/add.js';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('ss:cart:add', { detail: { item: data } }));
          flash(successLabel);
        })
        .catch(function () {
          flash(errorLabel);
        });
    });
  }

  /* ---------- init ---------- */

  function init(section) {
    if (!section || section.dataset.ssHeroInit === 'true') return;
    section.dataset.ssHeroInit = 'true';

    initGallery(section);
    var tiersApi = initTiers(section);
    initAddToCart(section, tiersApi);
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll(ROOT_SELECTOR)).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
